import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { IAnnualReport, IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import _, { isEqual } from 'lodash';
import { BehaviorSubject, Observable, Subscription, filter, find, map, shareReplay, take, tap } from 'rxjs';
import { AnnualReportProvider } from '../../../../../../../aumico-lib-annualreport-fe/src/lib';
import { DueDates } from '../../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/dueDate.interface';
import { DateLocalePipe } from '../../../../../../../aumico-lib-core-fe/src/lib';
import {
    AuthProvider,
    GetOrganizationDto,
    GetUserDto,
    OrganizationProvider,
} from '../../../../../../../aumico-lib-users-fe/src/lib';
import { ClosingEntryHelper } from '../domain/helpers/closingEntries.helper';
import { ExportSelection, FcpDataDTO, FcpExportHelper } from '../domain/helpers/export.helper';
import { FcpTreeItemMapper } from '../domain/mappers/fcpTreeItem.mapper';
import { FcpModel, FcpTreeItemType } from '../domain/models/fcp.model';
import { FcpTreeItem } from '../domain/models/fcpTreeItem.model';
import {
    exportingFcpData,
    getFcpChecklist,
    resetActiveItems,
    setActiveNavigation,
    setNavigationItems,
    setRootItem,
    setShowAdjustments,
    toggleActiveTestGroupItem,
    toggleActiveTestItem,
    unsetActiveTestItem,
} from '../domain/reducers/fcp.actions';
import {
    FcpFeatureState,
    hasContentToDisplay,
    selectActiveNavigationItem,
    selectActiveTestGroupItem,
    selectActiveTestItem,
    selectFcpItemToReset,
    selectFcpItemToUpdate,
    selectNavigationItems,
    selectShowAdjustments,
} from '../domain/reducers/fcp.reducer';
import { FcpClosingEntriesComponent } from './fcpClosingEntries/fcpClosingEntries.component';
@UntilDestroy()
@Component({
    selector: 'aumico-fcp-form',
    templateUrl: 'fcpForm.component.html',
    styleUrls: ['fcpForm.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinancialClosingProcessFormComponent implements OnInit, OnChanges, OnDestroy {
    @Input() domainModel: FcpModel;

    private domainModelSubject = new BehaviorSubject<FcpModel>(null);
    navigationItems$: Observable<FcpTreeItem[]>;
    activeNavigationItem$: Observable<FcpTreeItem>;
    activeTestGroupItem$: Observable<FcpTreeItem>;
    activeTestItem: FcpTreeItem;
    hasContent: boolean;
    fcpItemToReset: Subscription;
    fcpItemToUpdate: Subscription;
    previousNavigationItems: FcpTreeItem[] | null = null;
    previousActiveNavigationItem: FcpTreeItem | null = null;
    previousActiveTestGroupItem: FcpTreeItem | null = null;
    previousActiveTestItem: FcpTreeItem | null = null;
    previousFcpItemToReset: FcpTreeItem | null = null;
    previousFcpItemToUpdate: FcpTreeItem | null = null;
    dueDates: DueDates;
    currentUser$: Observable<GetUserDto>;
    currentUser: GetUserDto;
    showAdjustments$: Observable<boolean>;
    outputFinStatements: IFinStatements[];
    searchForm: FormGroup;
    groupsAndItemsForFilter: string[] = [];
    filteredGroupOrItem: string = '';
    showExportDropDown: boolean = false;
    menuPosition = { top: 0, right: 0 };
    exportSelection: ExportSelection = {
        unlabeledComments: true,
        internalComments: true,
        externalComments: true,
        reviewComments: true,
        pendencies: true,
        closing: true,
        exportExcel: true,
    };
    cardExpandedState: { [key: string]: boolean } = {
        checklist: true,
        comments: true,
        docs: true,
        closing: true,
    };
    annualReport: IAnnualReport;

    public activeTestGroupItem: FcpTreeItem;
    public dueDateCY: number;
    public dueDatePY: number;
    public tabsTitles: string[] = [];

    @Output() finalizeAndContinueButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
    authoringCompanyName: string;

    constructor(
        private readonly store: Store<FcpFeatureState>,
        private readonly arProvider: AnnualReportProvider,
        private readonly authProvider: AuthProvider,
        private readonly organizationProvider: OrganizationProvider,
        private readonly crd: ChangeDetectorRef,
        private fb: FormBuilder,
        private dateLocalPipe: DateLocalePipe,
        private i18nextService: I18NextService,
    ) {}

    ngOnDestroy(): void {
        this.store.dispatch(resetActiveItems());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['domainModel']) {
            const previousValue = changes['domainModel'].previousValue;
            const currentValue = changes['domainModel'].currentValue;

            if (!isEqual(previousValue, currentValue)) {
                this.domainModelSubject.next(this.domainModel);
                this.outputFinStatements = this.domainModel.outputFinStatements;
                this.updateRoot(this.domainModel);
                this.updateActiveNavigation(this.domainModel);
            }
        }
    }

    ngOnInit() {
        this.initializeForm();

        this.navigationItems$ = this.store.select(selectNavigationItems).pipe(
            untilDestroyed(this),
            tap((navigationItems) => {
                if (this.domainModel && navigationItems && !_.isEqual(navigationItems, this.previousNavigationItems)) {
                    navigationItems.forEach((item, index) => {
                        this.updateActiveTestItemInDomainModel(item);
                        const tabTitle =
                            item.name === 'Balance'
                                ? (this.i18nextService.t(
                                      'aumico_reports_closing_process_balance_navigation_tab',
                                  ) as string)
                                : (this.i18nextService.t(
                                      'aumico_reports_closing_process_income_navigation_tab',
                                  ) as string);
                        this.tabsTitles.push(tabTitle);
                    });
                    this.previousNavigationItems = _.cloneDeep(navigationItems); // Update the previous item
                }
            }),
        );
        this.store
            .select(hasContentToDisplay)
            .pipe(untilDestroyed(this))
            .subscribe((element) => (this.hasContent = element));
        this.activeNavigationItem$ = this.store.select(selectActiveNavigationItem).pipe(
            tap((activeItem) => {
                if (this.domainModel && activeItem && !_.isEqual(activeItem, this.previousActiveNavigationItem)) {
                    this.updateActiveTestItemInDomainModel(activeItem);
                    this.previousActiveNavigationItem = _.cloneDeep(activeItem); // Update the previous item
                }
            }),
        );

        this.store
            .select(selectActiveTestGroupItem)
            .pipe(untilDestroyed(this))
            .subscribe((activeItem) => {
                if (this.domainModel && activeItem && !_.isEqual(activeItem, this.previousActiveTestGroupItem)) {
                    this.updateActiveTestItemInDomainModel(activeItem);
                    this.previousActiveTestGroupItem = _.cloneDeep(activeItem); // Update the previous item
                    this.activeTestGroupItem = activeItem;
                }
            });

        this.store
            .select(selectActiveTestItem)
            .pipe(
                tap((activeTestItem) => {
                    this.activeTestItem = activeTestItem;
                    if (this.domainModel && activeTestItem && !_.isEqual(activeTestItem, this.previousActiveTestItem)) {
                        this.updateActiveTestItemInDomainModel(activeTestItem);
                        this.previousActiveTestItem = _.cloneDeep(activeTestItem); // Update the previous item
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.fcpItemToReset = this.store
            .select(selectFcpItemToReset)
            .pipe(
                untilDestroyed(this),
                tap((fcpItemToReset) => {
                    if (this.domainModel && fcpItemToReset && !_.isEqual(fcpItemToReset, this.previousFcpItemToReset)) {
                        this.updateActiveTestItemInDomainModel(fcpItemToReset);
                        this.previousFcpItemToReset = _.cloneDeep(fcpItemToReset); // Update the previous item
                    }
                }),
            )
            .subscribe();
        this.fcpItemToUpdate = this.store
            .select(selectFcpItemToUpdate)
            .pipe(
                untilDestroyed(this),
                tap((fcpItemToUpdate) => {
                    if (this.domainModel && fcpItemToUpdate) {
                        this.updateActiveTestItemInDomainModel(fcpItemToUpdate);
                        this.previousFcpItemToUpdate = _.cloneDeep(fcpItemToUpdate); // Update the previous item
                    }
                }),
            )
            .subscribe();
        this.showAdjustments$ = this.store.select(selectShowAdjustments).pipe(untilDestroyed(this));
        this.arProvider
            .getActiveDueDates$()
            .pipe(untilDestroyed(this))
            .subscribe((dueDates) => {
                this.dueDates = dueDates;
                this.dueDateCY = dueDates.dueDate.getFullYear();
                this.dueDatePY = dueDates.dueDatePY?.getFullYear();
            });
        this.arProvider
            .getActiveAnnualReport$()
            .pipe(untilDestroyed(this))
            .subscribe((ar) => {
                this.annualReport = ar;
            });
        this.currentUser$ = this.authProvider.getCurrentUser$().pipe(
            filter((user) => user != null),
            tap((user) => (this.currentUser = user)),
            shareReplay(),
        );
        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(
                find((authoringCompany: GetOrganizationDto) => authoringCompany != null),
                untilDestroyed(this),
            )
            .subscribe((authoringCompany) => {
                this.authoringCompanyName = authoringCompany.name;
            });

        this.store.dispatch(getFcpChecklist.loadChecklists());
        //Dispatch the action to get the current user so that the info can be used by the child components
        this.authProvider.dispatchGetCurrentUser();
        // Ensure the active navigation is set initially
        if (this.domainModel) {
            this.updateActiveNavigation(this.domainModel);
            this.setActiveTestItemsAndGroups(this.domainModel);
        }
    }

    private initializeForm() {
        this.searchForm = this.fb.group({
            searchTerm: [''],
        });
    }

    private getNavigationItems(domainModel: FcpModel): FcpTreeItem[] {
        if (!domainModel || !domainModel.items) {
            return [];
        }
        return FcpTreeItemMapper.filterItemsByType(
            FcpTreeItemMapper.flattenTree(domainModel.items),
            FcpTreeItemType.Navigation,
        );
    }
    private getRootItem(domainModel: FcpModel): FcpTreeItem {
        if (!domainModel || !domainModel.items) {
            return null;
        }
        const items = FcpTreeItemMapper.filterItemsByType(
            FcpTreeItemMapper.flattenTree(domainModel.items),
            FcpTreeItemType.Root,
        );
        return items.length > 0 ? items[0] : null;
    }

    // This is very important to update the active test item in the domain model
    // otherwise it will not be reflected in the saved data
    private updateActiveTestItemInDomainModel(activeTestItem: FcpTreeItem) {
        const updateItem = (items: FcpTreeItem[]): boolean => {
            for (let i = 0; i < items.length; i++) {
                if (items[i].uid === activeTestItem.uid) {
                    items[i] = { ...items[i], ...activeTestItem };
                    return true;
                }
                if (items[i].children && updateItem(items[i].children)) {
                    return true;
                }
            }
            return false;
        };

        updateItem(this.domainModel.items);
        this.crd.markForCheck();
        this.outputFinStatements = ClosingEntryHelper.applyAdjustmentsOnInputFinStatements(
            this.domainModel,
            this.dueDates,
        ).outputFinStatements;
    }

    private updateRoot(domainModel: FcpModel) {
        const root = this.getRootItem(domainModel);
        this.store.dispatch(setRootItem({ payload: root }));
    }
    private updateActiveNavigation(domainModel: FcpModel) {
        const navigationItems = this.getNavigationItems(domainModel);
        this.store.dispatch(setNavigationItems({ payload: navigationItems }));
    }

    private setActiveTestItemsAndGroups(domainModel: FcpModel) {
        const processItems = (items: FcpTreeItem[]): void => {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.active) {
                    if (item.type === FcpTreeItemType.TestGroup) {
                        this.store.dispatch(toggleActiveTestGroupItem({ payload: item }));
                    } else if (item.type === FcpTreeItemType.TestItem) {
                        this.store.dispatch(toggleActiveTestItem({ payload: item }));
                    }
                }
                if (item.children) {
                    processItems(item.children);
                }
            }
        };
        processItems(domainModel.items);
    }

    togglePreview() {
        this.showAdjustments$
            .pipe(
                map((showAdjustments) => !showAdjustments),
                take(1),
            )
            .subscribe((newShowAdjustmentsValue) => {
                this.store.dispatch(setShowAdjustments({ payload: newShowAdjustmentsValue }));
            });
    }

    reset() {
        this.arProvider.resetFcp();
        this.domainModel.items = [];
    }

    toggleExportDropDown(event?: MouseEvent) {
        if (!event) {
            this.showExportDropDown = false;
            return;
        }
        event.stopPropagation();
        this.showExportDropDown = !this.showExportDropDown;

        // Get the button element
        const buttonElement = event.target as HTMLElement;

        // Get the bounding rectangle of the button element
        const rect = buttonElement.getBoundingClientRect();

        // Calculate the position of the dropdown at the bottom right of the button
        const right = window.innerWidth - rect.right;
        const top = rect.bottom;

        // Set the menu position
        this.menuPosition = { top: top, right: right };
    }

    export() {
        this.showExportDropDown = false;

        const dueDate = this.dueDates.dueDate;

        // Call the export method in FcpExportHelper
        const result: boolean | FcpDataDTO = new FcpExportHelper(this.dateLocalPipe).export(
            this.exportSelection,
            this.domainModel.items,
            dueDate,
            this.annualReport,
            this.authoringCompanyName,
            this.currentUser,
        );
        if (this.exportSelection.exportExcel === false) {
            this.dispatchExportAnnualReportExcel(result as FcpDataDTO);
        }
    }

    public dispatchExportAnnualReportExcel(dataToExport: FcpDataDTO): void {
        let fileName = 'AumicoExport';
        try {
            fileName =
                this.annualReport?.generalInformation.company.name +
                ', ' +
                this.dateLocalPipe.transform(this.dueDates.dueDate)?.toString();
        } catch (error) {}
        this.store.dispatch(
            exportingFcpData.exportingFcpData({
                payload: { data: dataToExport, fileName },
            }),
        );
    }

    setActiveNavigation(item: FcpTreeItem) {
        this.store.dispatch(setActiveNavigation({ payload: item }));
    }

    filterTestItems() {
        this.store.dispatch(unsetActiveTestItem());
        this.filteredGroupOrItem = this.searchForm.value.searchTerm;
    }

    resetFilter() {
        this.filteredGroupOrItem = '';
    }

    updateCardExpandedState(cardKey: string, expanded: boolean) {
        this.cardExpandedState[cardKey] = expanded;
    }

    public finalizeAndContinue(): void {
        this.finalizeAndContinueButtonClicked.emit(true);
    }

    @ViewChild('closingEntries') closingEntriesComponent!: FcpClosingEntriesComponent;

    // Method that is called when the Add button is clicked
    handleAddClosingEntry() {
        if (this.closingEntriesComponent) {
            this.closingEntriesComponent.showAddClosingEntryForm(); // Call the child component's method
        }
    }
}
